import axios from "axios";
import { useState } from "react";
import toast from "react-hot-toast";

const SendIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="10" cy="7" r="3" />
    <path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <rect x="2" y="4" width="16" height="12" rx="2" />
    <path d="M2 7l8 5 8-5" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M4 3h3l1.5 4-2 1.5a10 10 0 004 4L12 10l4 1.5V15a2 2 0 01-2 2C6.5 17 3 11 3 5a2 2 0 011-2z" />
  </svg>
);

const ProjectIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <rect x="2" y="3" width="16" height="14" rx="2" />
    <path d="M6 7h8M6 10h8M6 13h5" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H6l-4 3V5z" />
  </svg>
);

const projectTypes = [
  "Rooftop Solar Installation",
  "Ground Mount Solar",
  "Solar Panel Mounting Structure",
  "Solar Water Heater",
  "Off-Grid Solar System",
  "Hybrid Solar System",
  "Commercial Solar Project",
  "Other",
];

export default function InquiryForm({ serviceTitle = "" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    number: "",
    projectType: "",
    query: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handle = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.number.trim()) e.number = "Phone number is required";
    else if (!/^\+?[\d\s\-]{8,15}$/.test(form.number)) e.number = "Invalid phone number";
    if (!form.projectType) e.projectType = "Select a project type";
    if (!form.query.trim()) e.query = "Message is required";
    return e;
  };

  const handleSubmit = async () => {
    try {
          const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setLoading(true);
      await axios.post(
      `${process.env.REACT_APP_API_URL}/enquiry/create`,
      form
    );

    toast.success("Inquiry submitted successfully!");
    } catch (error) {
        console.log("Error", error);
    }finally{
        setLoading(false);
        setSubmitted(true);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", number: "", projectType: "", query: "" });
    setErrors({});
    setSubmitted(false);
  };

  // ── Field wrapper ──
  const Field = ({ icon: Icon, error, children }) => (
    <div className="flex flex-col gap-1">
      <div
        className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 transition-all duration-200"
        style={{
          background: "#f0f5ee",
          border: `1.5px solid ${error ? "#f87171" : "transparent"}`,
          outline: "none",
        }}
      >
        <span style={{ color: error ? "#f87171" : "#4ade80", flexShrink: 0 }}>
          <Icon />
        </span>
        {children}
      </div>
      {error && (
        <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
          <span>⚠</span> {error}
        </p>
      )}
    </div>
  );

  const inputStyle = {
    background: "transparent",
    outline: "none",
    border: "none",
    width: "100%",
    fontSize: "13px",
    color: "#1a2e0a",
    fontFamily: "inherit",
  };

  return (
    <div
      className=" rounded-3xl overflow-hidden"
      style={{
        background: "white",
        border: "1.5px solid #e0ead8",
        boxShadow: "0 8px 40px rgba(13,45,13,0.10)",
        fontFamily: "'Barlow', 'Montserrat', sans-serif",
      }}
    >
      {/* Header */}
      <div
        className="px-6 py-5 relative overflow-hidden"
        style={{ background: "#89ea5f" }}
      >
        {/* Dot pattern */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(74,222,128,0.08) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)" }}
            >
              <SendIcon />
            </div>
            <h3 className="text-gray-800 font-black text-base">Get a Free Quote</h3>
          </div>
          <p className="text-gray-600 mt-2 text-xs leading-relaxed">
            Fill in the details and our team will get back to you within 24 hours.
          </p>
          {serviceTitle && (
            <div
              className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold truncate bg-green-400 text-gray-700"
              style={{ border: "1px solid rgba(74,222,128,0.2)" }}
            >
              📋 {serviceTitle}
            </div>
          )}
        </div>
      </div>

      {/* Form body */}
      <div className="px-6 py-6">
        {submitted ? (
          /* Success state */
          <div className="flex flex-col items-center text-center py-6 gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", boxShadow: "0 4px 20px rgba(74,222,128,0.4)" }}
            >
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <path d="M5 12l5 5L20 7" />
              </svg>
            </div>
            <div>
              <p className="text-gray-900 font-black text-lg">Inquiry Sent!</p>
              <p className="text-gray-500 text-sm mt-1">We'll reach out to you within 24 hours.</p>
            </div>
            <button
              onClick={reset}
              className="text-xs font-bold px-5 py-2.5 rounded-xl transition-all duration-200 hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", color: "#0d2d0d" }}
            >
              Send Another
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">

            {/* Name */}
             <div 
               style={{
                    background: "#f0f5ee",
                    border: `1.5px solid ${errors.name ? "#f87171" : "transparent"}`,
                    outline: "none",
                    }}
                className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 transition-all duration-200" >
                <span style={{ color: errors.name ? "#f87171" : "#4ade80", flexShrink: 0 }}>
                        <UserIcon />
                 </span>
             <input
                name="name"
                value={form.name}
                onChange={handle}
                placeholder="Enter Your Full Name"
                className="outline-none bg-transparent text-[13px] border-none text-[#1a2e0a]"
              />
              {errors.name && (
        <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
          <span>⚠</span> {errors.name}
        </p>
      )}
              </div>

            {/* Email */}
             <div 
               style={{
                    background: "#f0f5ee",
                    border: `1.5px solid ${errors.email ? "#f87171" : "transparent"}`,
                    outline: "none",
                    }}
                className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 transition-all duration-200" >
                <span style={{ color: errors.email ? "#f87171" : "#4ade80", flexShrink: 0 }}>
                        <MailIcon />
                 </span>
             <input
                type="email"
                name="email"
                value={form.email}
                onChange={handle}
                 placeholder="Email address"
                className="outline-none bg-transparent text-[13px] border-none text-[#1a2e0a]"
              />
              {errors.email && (
                    <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
                    <span>⚠</span> {errors.email}
                    </p>
                )}
              </div>

            <div 
               style={{
                    background: "#f0f5ee",
                    border: `1.5px solid ${errors.number ? "#f87171" : "transparent"}`,
                    outline: "none",
                    }}
                className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 transition-all duration-200" >
                <span style={{ color: errors.number ? "#f87171" : "#4ade80", flexShrink: 0 }}>
                        <PhoneIcon />
                 </span>
             <input
              type="text"
                name="number"
                value={form.number}
                onChange={handle}
                placeholder="Phone number"
                className="outline-none bg-transparent text-[13px] border-none text-[#1a2e0a]"
              />
              {errors.number && (
        <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
          <span>⚠</span> {errors.number}
        </p>
      )}
              </div>

            {/* Project Type */}
            <Field icon={ProjectIcon} error={errors.projectType}>
              <select
                name="projectType"
                value={form.projectType}
                onChange={handle}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="" disabled>Select project type</option>
                {projectTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            {/* Message */}
            <div className="flex flex-col gap-1">
              <div
                className="flex items-start gap-2.5 rounded-xl px-3.5 py-3 transition-all duration-200"
                style={{
                  background: "#f0f5ee",
                  border: `1.5px solid ${errors.query ? "#f87171" : "transparent"}`,
                }}
              >
                <span style={{ color: errors.query ? "#f87171" : "#4ade80", flexShrink: 0, marginTop: "2px" }}>
                  <MessageIcon />
                </span>
                <textarea
                  name="query"
                  value={form.query}
                  onChange={handle}
                  placeholder="Tell us about your project..."
                  rows={4}
                  style={{ ...inputStyle, resize: "none", lineHeight: 1.6 }}
                />
              </div>
              {errors.query && (
                <p className="text-red-400 text-xs ml-1">⚠ {errors.query}</p>
              )}
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-black text-sm tracking-wide transition-all duration-200 flex items-center justify-center gap-2 mt-1"
              style={{
                background: loading
                  ? "#a3d9a5"
                  : "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)",
                color: "#0d2d0d",
                boxShadow: loading ? "none" : "0 4px 16px rgba(74,222,128,0.35)",
                cursor: loading ? "not-allowed" : "pointer",
                transform: loading ? "none" : undefined,
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-green-800 border-t-transparent animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <SendIcon />
                  Submit Inquiry
                </>
              )}
            </button>

            {/* Trust line */}
            <p className="text-center text-gray-400 text-xs">
              🔒 Your information is safe with us
            </p>
          </div>
        )}
      </div>
    </div>
  );
}